import { useState, useEffect, useCallback } from 'react';
import {
  Brain, TrendingUp, TrendingDown, RefreshCw, Info, Target,
  AlertTriangle, Clock,
} from 'lucide-react';
// Forecast page decoupled from bot system — uses symbol-direct market data.
import { Card, CardHeader, Spinner, Badge, InfoTip } from '../components/ui';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import * as api from '../services/api';

function ProbabilityRing({ probUp, size = 120 }) {
  const r = 46;
  const circ = 2 * Math.PI * r;
  const safeUp = Number.isFinite(probUp) ? Math.max(0, Math.min(100, probUp)) : 0;
  const upDash = (safeUp / 100) * circ;
  return (
    <svg width={size} height={size} viewBox="0 0 100 100">
      <circle cx="50" cy="50" r={r} fill="none" stroke="#374151" strokeWidth="8" />
      <circle cx="50" cy="50" r={r} fill="none" stroke="#ef4444" strokeWidth="8"
        strokeDasharray={`${circ} ${circ}`} strokeDashoffset={0}
        transform="rotate(-90 50 50)" />
      <circle cx="50" cy="50" r={r} fill="none" stroke="#22c55e" strokeWidth="8"
        strokeDasharray={`${upDash} ${circ - upDash}`} strokeDashoffset={0}
        transform="rotate(-90 50 50)" />
      <text x="50" y="45" textAnchor="middle" fill="#22c55e" fontSize="16" fontWeight="bold">
        {Number.isFinite(probUp) ? `${probUp}%` : '—'}
      </text>
      <text x="50" y="60" textAnchor="middle" fill="#9ca3af" fontSize="8">UP</text>
    </svg>
  );
}

function RiskBadge({ level }) {
  if (!level) return <span className="text-xs px-2 py-0.5 rounded-full font-medium text-gray-500 bg-gray-700/30">— risk</span>;
  const colors = { Low: 'text-emerald-400 bg-emerald-500/20', Medium: 'text-amber-400 bg-amber-500/20', High: 'text-red-400 bg-red-500/20', Extreme: 'text-red-400 bg-red-500/30 animate-pulse' };
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${colors[level] || colors.Medium}`}>{level}</span>;
}

function RegimeBadge({ regime }) {
  if (!regime) return <span className="text-xs px-2 py-0.5 rounded-full font-medium text-gray-500 bg-gray-700/30">— regime</span>;
  const colors = {
    'Trend-Up': 'text-emerald-400 bg-emerald-500/20',
    'Trend-Down': 'text-red-400 bg-red-500/20',
    'Sideways': 'text-gray-400 bg-gray-500/20',
    'High-Volatility': 'text-orange-400 bg-orange-500/20',
    'Accumulation': 'text-blue-400 bg-blue-500/20',
    'Distribution': 'text-purple-400 bg-purple-500/20'
  };
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${colors[regime] || colors.Sideways}`}>{regime}</span>;
}

// Small helpers so we never render "undefined" or fabricate numbers.
const fmtPct = (v) => Number.isFinite(v) ? `${v >= 0 ? '+' : ''}${v}%` : '—';
const fmtNum = (v, dp = 6) => Number.isFinite(v) ? v.toFixed(dp) : '—';
const fmtProb = (v) => Number.isFinite(v) ? `${v}%` : '—';

const POPULAR_PAIRS = [
  'BTC/USDT', 'ETH/USDT', 'SOL/USDT', 'BNB/USDT', 'XRP/USDT', 'DOGE/USDT',
  'ADA/USDT', 'AVAX/USDT', 'LINK/USDT', 'DOT/USDT', 'MATIC/USDT', 'ARB/USDT',
];

export default function ForecastPage() {
  const [symbol, setSymbol] = useState('BTC/USDT');
  const [customSymbol, setCustomSymbol] = useState('');
  const [horizon, setHorizon] = useState('4h');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    if (!symbol) { setData(null); setLoading(false); return; }
    setLoading(true);
    try {
      const result = await api.getMarketForecast(symbol);
      // Backend wraps the payload in {success, data, timestamp}; the page expects
      // the inner shape (summary, monteCarlo, etc.), so unwrap when present.
      setData(result?.data || result);
    } catch (err) {
      setData({ dataSource: 'unavailable', reason: err?.message });
    } finally {
      setLoading(false);
    }
  }, [symbol]);

  useEffect(() => { fetchData(); }, [fetchData]);

  function applyCustomSymbol() {
    const s = customSymbol.trim().toUpperCase();
    if (!s) return;
    setSymbol(s.includes('/') ? s : s + '/USDT');
    setCustomSymbol('');
  }

  const d = data || {};
  const dataUnavailable = d.dataSource && d.dataSource !== 'real';
  const summary = d.summary || {};
  const mc = d.monteCarlo || {};
  const horizonData = summary.forecasts?.[horizon] || {};

  // Build Monte Carlo chart data — ONLY if the engine produced real percentile lines.
  // Each step (i) is the i-th hour ahead. Sample paths render as faint trajectories.
  const mcChartData = (mc.percentileLines?.p50?.length)
    ? Array.from({ length: mc.percentileLines.p50.length }, (_, i) => {
        const obj = { step: i };
        if (mc.percentileLines.p5?.[i]  !== undefined) obj.p5  = mc.percentileLines.p5[i];
        if (mc.percentileLines.p50?.[i] !== undefined) obj.p50 = mc.percentileLines.p50[i];
        if (mc.percentileLines.p95?.[i] !== undefined) obj.p95 = mc.percentileLines.p95[i];
        if (Array.isArray(mc.paths)) {
          mc.paths.slice(0, 8).forEach((path, j) => { if (path[i] !== undefined) obj[`path${j}`] = path[i]; });
        }
        return obj;
      })
    : [];

  if (loading && !data) return (
    <div className="flex items-center justify-center h-64"><Spinner size="lg" className="text-accent-primary" /></div>
  );

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-accent-primary">Probability Forecasting Engine</h1>
          <p className="text-gray-400">Bayesian probability + Monte Carlo simulation on real OHLCV</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <select value={symbol} onChange={e => setSymbol(e.target.value)} className="input w-auto text-sm">
            {POPULAR_PAIRS.map(p => <option key={p} value={p}>{p}</option>)}
            {!POPULAR_PAIRS.includes(symbol) && <option value={symbol}>{symbol}</option>}
          </select>
          <div className="flex bg-dark-700 rounded-lg overflow-hidden">
            {['1h', '4h', '24h'].map(h => (
              <button key={h} onClick={() => setHorizon(h)} className={`px-3 py-1.5 text-sm transition-colors ${horizon === h ? 'bg-accent-primary text-white' : 'text-gray-400 hover:text-white'}`}>{h}</button>
            ))}
          </div>
          <button onClick={fetchData} className="btn-ghost p-2"><RefreshCw className="w-4 h-4" /></button>
        </div>
      </div>

      {/* Custom symbol entry */}
      <div className="flex items-center gap-2 max-w-md">
        <input
          type="text" value={customSymbol}
          onChange={e => setCustomSymbol(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && applyCustomSymbol()}
          placeholder="Custom symbol — e.g. ATOM/USDT, ENS/USDT"
          className="flex-1 bg-dark-800 border border-dark-700 rounded-xl px-3 py-2 text-xs text-white placeholder-gray-600 focus:border-amber-500 focus:outline-none"
        />
        <button onClick={applyCustomSymbol} disabled={!customSymbol.trim()}
          className="px-3 py-2 rounded-xl text-xs font-bold bg-amber-500/15 text-amber-400 border border-amber-500/30 hover:bg-amber-500/25 disabled:opacity-30">
          Load
        </button>
      </div>

      {/* Data unavailable notice */}
      {dataUnavailable && (
        <div className="bg-amber-500/5 border border-amber-500/20 rounded-2xl px-4 py-3 flex items-start gap-3">
          <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-300">Forecast unavailable for {symbol}</p>
            <p className="text-xs text-amber-300/80 mt-0.5">{d.message || d.reason || 'Real OHLCV data is required for forecasting and is not available for this trading pair. Try a major pair like BTC/USDT, ETH/USDT, SOL/USDT.'}</p>
          </div>
        </div>
      )}

      {!dataUnavailable && (
      <>
      {/* Probability + Summary row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Probability Wheel */}
        <Card className="flex flex-col items-center py-6">
          <p className="text-gray-400 text-sm mb-3 flex items-center gap-1.5">
            Price Probability ({horizon})
            <InfoTip side="bottom">Probability that price closes HIGHER (UP) or LOWER (DOWN) than now at the end of the {horizon} horizon. 70%+ = strong directional signal. 50-60% = barely above coin-flip, NOT a tradeable edge alone.</InfoTip>
          </p>
          <ProbabilityRing probUp={horizonData.probUp} size={140} />
          <div className="flex gap-6 mt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-emerald-400">{fmtProb(horizonData.probUp)}</p>
              <p className="text-xs text-gray-500">UP</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-red-400">{fmtProb(horizonData.probDown)}</p>
              <p className="text-xs text-gray-500">DOWN</p>
            </div>
          </div>
        </Card>

        {/* Forecast Summary Box */}
        <Card className="lg:col-span-2">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <Brain className="w-5 h-5 text-accent-primary" />
            <h3 className="font-semibold text-white">Forecast Summary</h3>
            <RiskBadge level={summary.riskLevel} />
            <RegimeBadge regime={summary.marketRegime} />
            {summary.directionalBias && (
              <span className={`ml-2 text-sm font-bold ${summary.directionalBias === 'Bullish' ? 'text-emerald-400' : summary.directionalBias === 'Bearish' ? 'text-red-400' : 'text-amber-400'}`}>
                {summary.directionalBias === 'Bullish' ? '↑' : summary.directionalBias === 'Bearish' ? '↓' : '→'} {summary.directionalBias}
              </span>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-dark-700 rounded-lg p-3">
              <p className="text-gray-400 text-xs flex items-center gap-1.5">
                Expected Change ({horizon})
                <InfoTip side="bottom">The MEAN of the probability distribution — center of the bell curve, NOT a guaranteed move. The Range below shows the typical ±band; actual moves outside that range happen ~30% of the time.</InfoTip>
              </p>
              {Number.isFinite(horizonData.expectedChange) ? (
                <p className={`text-lg font-bold ${horizonData.expectedChange >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {fmtPct(horizonData.expectedChange)}
                </p>
              ) : (
                <p className="text-lg font-bold text-gray-500">—</p>
              )}
              {horizonData.expectedRange && (
                <p className="text-xs text-gray-500">
                  Range: {fmtPct(horizonData.expectedRange.low)} to {fmtPct(horizonData.expectedRange.high)}
                </p>
              )}
            </div>
            <div className="bg-dark-700 rounded-lg p-3">
              <p className="text-gray-400 text-xs flex items-center gap-1.5">
                Breakout Probability
                <InfoTip side="bottom">Probability that price breaks out of its recent range during the forecast window. High breakout + High volatility = volatile expansion likely. Low breakout + Sideways regime = expect chop, range strategies fit.</InfoTip>
              </p>
              <p className="text-lg font-bold text-amber-400">{fmtProb(summary.probBreakout)}</p>
              {summary.marketRegime && <p className="text-xs text-gray-500">Regime: {summary.marketRegime}</p>}
            </div>
          </div>

          {summary.insightText && (
            <div className="bg-dark-800 rounded-lg p-3 border border-accent-primary/20">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-accent-primary mt-0.5 flex-shrink-0" />
                <p className="text-sm text-gray-300 leading-relaxed">{summary.insightText}</p>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Forecast Cards by Timeframe */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {['1h', '4h', '24h'].map(h => {
          const f = summary.forecasts?.[h];
          const hasData = f && Number.isFinite(f.probUp);
          return (
            <Card key={h} className={`border-2 transition-all ${horizon === h ? 'border-accent-primary/40' : 'border-transparent'}`}>
              <button className="w-full text-left" onClick={() => setHorizon(h)}>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-accent-primary font-semibold">Next {h}</p>
                  {hasData && f.confidence ? (
                    <span className={`text-xs px-2 py-0.5 rounded-full ${f.confidence === 'high' ? 'bg-emerald-500/20 text-emerald-400' : f.confidence === 'medium' ? 'bg-amber-500/20 text-amber-400' : 'bg-gray-500/20 text-gray-400'}`}>
                      {String(f.confidence).toUpperCase()}
                    </span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-700/30 text-gray-500">PENDING</span>
                  )}
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Up</span>
                    <span className="text-emerald-400 font-bold">{hasData ? fmtProb(f.probUp) : '—'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Down</span>
                    <span className="text-red-400 font-bold">{hasData ? fmtProb(f.probDown) : '—'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Expected</span>
                    <span className={hasData && f.expectedChange >= 0 ? 'text-emerald-400' : hasData ? 'text-red-400' : 'text-gray-500'}>
                      {hasData ? fmtPct(f.expectedChange) : '—'}
                    </span>
                  </div>
                </div>
              </button>
            </Card>
          );
        })}
      </div>

      {/* Monte Carlo Chart — only renders when real percentile data is present */}
      <Card>
        <CardHeader title="Monte Carlo Simulation" subtitle="1,000 simulated price paths — 5th, 50th, 95th percentile bands" />
        {mcChartData.length === 0 ? (
          <div className="text-center py-12">
            <Clock className="w-8 h-8 text-gray-600 mx-auto mb-2" />
            <p className="text-sm text-gray-500">Monte Carlo simulation pending — refresh once price data is loaded.</p>
          </div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={mcChartData}>
                <XAxis dataKey="step" tick={{ fill: '#6b7280', fontSize: 10 }} label={{ value: 'Hours ahead', fill: '#6b7280', fontSize: 11, position: 'insideBottom', offset: -3 }} />
                <YAxis tick={{ fill: '#6b7280', fontSize: 10 }} tickFormatter={v => Number.isFinite(v) ? v.toFixed(5) : ''} width={70} domain={['auto', 'auto']} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #374151', borderRadius: '8px', fontSize: 11 }}
                  formatter={(v, name) => [Number.isFinite(v) ? v.toFixed(8) : '—', name]}
                />
                {/* Faint sample paths — only render keys that the data actually contains */}
                {Array.from({ length: 8 }, (_, j) => (
                  <Line key={j} type="monotone" dataKey={`path${j}`} stroke="#374151" strokeWidth={0.8} dot={false} opacity={0.4} legendType="none" connectNulls={false} />
                ))}
                <Line type="monotone" dataKey="p5" stroke="#ef4444" strokeWidth={1.5} dot={false} strokeDasharray="4 4" name="5th Pct (Worst)" />
                <Line type="monotone" dataKey="p50" stroke="#22c55e" strokeWidth={2.5} dot={false} name="50th Pct (Median)" />
                <Line type="monotone" dataKey="p95" stroke="#818cf8" strokeWidth={1.5} dot={false} strokeDasharray="4 4" name="95th Pct (Best)" />
              </LineChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap gap-6 mt-3 text-xs text-gray-400 justify-center">
              <span className="flex items-center gap-2"><span className="w-4 h-0.5 bg-red-500 inline-block" /> Worst Case (5th)</span>
              <span className="flex items-center gap-2"><span className="w-4 h-0.5 bg-emerald-500 inline-block" /> Median (50th)</span>
              <span className="flex items-center gap-2"><span className="w-4 h-0.5 bg-purple-400 inline-block" /> Best Case (95th)</span>
            </div>
            <div className="grid grid-cols-3 gap-3 mt-4 text-center">
              <div className="bg-red-500/10 rounded-lg p-2">
                <p className="text-xs text-gray-400 flex items-center justify-center gap-1.5">
                  Worst Case
                  <InfoTip side="top">5th percentile — only 5% of simulations went WORSE than this. Use as your defensive stop-loss reference. Going below this is a tail event.</InfoTip>
                </p>
                <p className="text-sm font-bold text-red-400">{fmtNum(mc.percentile5)}</p>
              </div>
              <div className="bg-emerald-500/10 rounded-lg p-2">
                <p className="text-xs text-gray-400 flex items-center justify-center gap-1.5">
                  Median
                  <InfoTip side="top">50th percentile — the middle outcome. Half of simulations ended below it, half above. DON'T set TP here expecting to reach it; price gets here only ~50% of the time. Use 65-75th percentile for realistic TP.</InfoTip>
                </p>
                <p className="text-sm font-bold text-emerald-400">{fmtNum(mc.percentile50)}</p>
              </div>
              <div className="bg-purple-500/10 rounded-lg p-2">
                <p className="text-xs text-gray-400 flex items-center justify-center gap-1.5">
                  Best Case
                  <InfoTip side="top">95th percentile — only 5% of simulations went BETTER than this. Use as your moon-shot upside scenario, but plan around the median for realistic TP.</InfoTip>
                </p>
                <p className="text-sm font-bold text-purple-400">{fmtNum(mc.percentile95)}</p>
              </div>
            </div>
          </>
        )}
      </Card>
      </>
      )}
    </div>
  );
}
